import React, { useMemo, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { AirlineType } from '../../../enums/enum';
import {
  DownloadPacketsFormData,
  PacketDownloadResult,
} from '../../../@types/FlightBriefing.type';
import { datesCreator } from '../../../utils/dateHandler';
import { useAuth } from '../../../component/context/AuthContext/AuthContext';
import useDownloadPacket from '../../../customHooks/useDownloadPacket/useDownloadPacket';
import FlightSelectCard from './FlightSelectCard';
import PacketTypeTable from './PacketTypeTable';
import { getPacketDisplayName, PACKET_TYPES } from './packetTypes';
import {
  FormErrors,
  toFlightBriefingParams,
  validateDownloadForm,
} from './downloadForm';
import BetaAlert, { BetaAlertSeverity } from '../../components/BetaAlert';
import './DownloadPackets.scss';

interface Notice {
  id: string;
  severity: BetaAlertSeverity;
  title: string;
  message: React.ReactNode;
  detail?: string;
}

/** Alerts close automatically after this time (paused while hovered). */
export const NOTICE_DURATION_MS = 10000;

export const DOWNLOAD_NOTICES = {
  success: {
    title: 'Download successful',
    message: 'Downloaded to your device:',
  },
  error: {
    title: 'Download failed',
    message: 'Could not download:',
  },
  noDocuments: 'No documents could be downloaded.',
  warningTitle: 'Check your selection',
} as const;

const NoticeList: React.FC<{
  intro: string;
  items: string[];
  variant?: 'chips' | 'list';
}> = ({ intro, items, variant = 'chips' }) => (
  <>
    {intro}
    <ul className={`dp-notice__list dp-notice__list--${variant}`}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </>
);

const toNotices = (result: PacketDownloadResult): Notice[] => {
  const notices: Notice[] = [];
  if (result.downloaded.length > 0) {
    notices.push({
      id: 'success',
      severity: 'success',
      title: DOWNLOAD_NOTICES.success.title,
      message: (
        <NoticeList
          intro={DOWNLOAD_NOTICES.success.message}
          items={result.downloaded.map(getPacketDisplayName)}
        />
      ),
      detail: result.fileName,
    });
  }
  if (result.failures.length > 0) {
    const errorList = (
      <NoticeList
        intro={DOWNLOAD_NOTICES.error.message}
        variant="list"
        items={result.failures.map(
          (f) => `${getPacketDisplayName(f.packetType)}: ${f.message}`,
        )}
      />
    );
    notices.push({
      id: 'error',
      severity: 'error',
      title: DOWNLOAD_NOTICES.error.title,
      message:
        result.downloaded.length === 0 ? (
          <>
            <strong>{DOWNLOAD_NOTICES.noDocuments}</strong> {errorList}
          </>
        ) : (
          errorList
        ),
    });
  }
  return notices;
};

const DownloadPackets: React.FC = () => {
  const { userData } = useAuth();
  const dates = useMemo(() => datesCreator(), []);
  const [form, setForm] = useState<DownloadPacketsFormData>({
    airline: AirlineType.AS,
    flightNumber: '',
    origin: '',
    destination: '',
    flightDate: dates[1],
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [notices, setNotices] = useState<Notice[]>([]);
  // Bumped for every new set of alerts so their 10s timers restart.
  const [noticeBatch, setNoticeBatch] = useState(0);
  const { mutate, isPending } = useDownloadPacket();

  const showNotices = (list: Notice[]) => {
    setNoticeBatch((batch) => batch + 1);
    setNotices(list);
  };

  const handleChange = (
    field: keyof DownloadPacketsFormData,
    value: string,
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const toggle = (value: string) =>
    setSelected((current) =>
      current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    );

  const toggleAll = (checked: boolean) =>
    setSelected(checked ? PACKET_TYPES.map((p) => p.value) : []);

  const allSelected = PACKET_TYPES.every((p) => selected.includes(p.value));

  const warn = (message: string) =>
    showNotices([
      {
        id: 'warning',
        severity: 'warning',
        title: DOWNLOAD_NOTICES.warningTitle,
        message,
      },
    ]);

  const handleDownload = () => {
    const validationErrors = validateDownloadForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      warn('Please correct the highlighted flight details.');
      return;
    }
    if (selected.length === 0) {
      warn('Select at least one packet type to download.');
      return;
    }

    showNotices([]);
    mutate(
      {
        params: toFlightBriefingParams(form),
        packetTypes: PACKET_TYPES.filter((p) => selected.includes(p.value)),
        accessToken: userData?.access_token as string,
      },
      {
        onSuccess: (result) => showNotices(toNotices(result)),
        onError: (error) =>
          showNotices([
            {
              id: 'error',
              severity: 'error',
              title: DOWNLOAD_NOTICES.error.title,
              message: DOWNLOAD_NOTICES.noDocuments,
              detail: error.message,
            },
          ]),
      },
    );
  };

  const dismiss = (id: string) =>
    setNotices((current) => current.filter((n) => n.id !== id));

  const documentWord = selected.length === 1 ? 'document' : 'documents';
  const downloadingLabel = `Downloading ${selected.length} ${documentWord}…`;

  return (
    <div className="download-packets" data-testid="download-packets">
      {notices.length > 0 && (
        <div className="dp-notice">
          {notices.map((notice) => (
            <BetaAlert
              key={`${noticeBatch}-${notice.id}`}
              severity={notice.severity}
              title={notice.title}
              message={notice.message}
              detail={notice.detail}
              onClose={() => dismiss(notice.id)}
              autoHideDuration={NOTICE_DURATION_MS}
              data-testid={`download-notice-${notice.id}`}
            />
          ))}
        </div>
      )}
      <fieldset className="dp-fieldset" disabled={isPending}>
        <FlightSelectCard
          form={form}
          errors={errors}
          dates={dates}
          onChange={handleChange}
        />
        <PacketTypeTable
          packetTypes={PACKET_TYPES}
          selected={selected}
          disabled={isPending}
          onToggle={toggle}
          onToggleAll={toggleAll}
        />
      </fieldset>

      <div className="dp-actions">
        <Button
          variant="outlined"
          onClick={() => toggleAll(!allSelected)}
          disabled={isPending}
          data-testid="select-all-button"
        >
          {allSelected ? 'Unselect all' : 'Select all'}
        </Button>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={isPending}
          aria-busy={isPending}
          startIcon={
            isPending ? (
              <CircularProgress
                size={16}
                color="inherit"
                data-testid="download-progress"
              />
            ) : undefined
          }
          data-testid="download-button"
        >
          {isPending ? downloadingLabel : 'Download'}
        </Button>
      </div>
    </div>
  );
};

export default DownloadPackets;
